import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, Loader2, MessageSquare, Users } from "lucide-react";
import {
  getConversa,
  listMensagens,
  enviarTexto,
  enviarMidia,
  marcarLido as apiMarcarLido,
  nomeConversa,
  formatarTelefone,
} from "../services/whatsappChatApi";
import MessageBubble, { timestampMsg } from "./MessageBubble";
import ChatInput from "./ChatInput";
import { Avatar } from "./ConversasList";

const LIMIT = 50;

function rotularDia(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const hoje = new Date();
  const ontem = new Date();
  ontem.setDate(hoje.getDate() - 1);
  const sameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (sameDay(d, hoje)) return "HOJE";
  if (sameDay(d, ontem)) return "ONTEM";
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" });
}

function chaveDia(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function DiaSeparador({ iso }) {
  return (
    <div className="flex justify-center my-3 px-3">
      <span className="text-[11px] font-semibold tracking-wider uppercase text-ink-4 bg-surface-2/80 border border-white/[0.06] rounded-full px-3 py-1 select-none">
        {rotularDia(iso)}
      </span>
    </div>
  );
}

function tipoPorMime(mime) {
  if (!mime) return "document";
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/")) return "video";
  if (mime.startsWith("audio/")) return "audio";
  return "document";
}

export default function ChatView({ sessaoId, conversaId, socket, onVoltar, gravacaoDesligada }) {
  const [conversa, setConversa] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mensagens, setMensagens] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [enviando, setEnviando] = useState(false);

  const listRef = useRef(null);
  const topo = useRef(null);
  const primeiraVez = useRef(true);
  const proximoDoFundoRef = useRef(true);
  const ultimoMarcarLidoRef = useRef(0);

  const rolarFundo = useCallback(() => {
    requestAnimationFrame(() => {
      if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    });
  }, []);

  const handleScroll = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    proximoDoFundoRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  }, []);

  const marcarLidoThrottled = useCallback((cid) => {
    const agora = Date.now();
    if (agora - ultimoMarcarLidoRef.current < 3000) return;
    ultimoMarcarLidoRef.current = agora;
    apiMarcarLido(sessaoId, cid).catch(() => {});
  }, [sessaoId]);

  const carregarMensagens = useCallback(async (cursorParam = undefined) => {
    if (!conversaId) return;
    setCarregando(true);
    try {
      const el = listRef.current;
      const alturaAntes = el ? el.scrollHeight : 0;
      const { items, nextCursor, hasMore: more } = await listMensagens(sessaoId, conversaId, {
        limit: LIMIT,
        cursor: cursorParam || undefined,
      });
      setHasMore(more);
      setCursor(nextCursor);
      const asc = [...items].reverse();
      if (cursorParam) {
        setMensagens((prev) => {
          const ids = new Set(prev.map((m) => m.id));
          return [...asc.filter((m) => !ids.has(m.id)), ...prev];
        });
        // Mantém a posição visual ao prepender.
        requestAnimationFrame(() => {
          if (el) el.scrollTop += el.scrollHeight - alturaAntes;
        });
      } else {
        setMensagens(asc);
        primeiraVez.current = true;
      }
    } catch {
      // falha silenciosa
    } finally {
      setCarregando(false);
    }
  }, [sessaoId, conversaId]);

  useEffect(() => {
    if (!conversaId) {
      setConversa(null);
      setMensagens([]);
      return;
    }
    let cancelado = false;
    setLoading(true);
    setMensagens([]);
    setHasMore(false);
    setCursor(null);

    getConversa(sessaoId, conversaId)
      .then((d) => { if (!cancelado) setConversa(d); })
      .catch(() => { if (!cancelado) setConversa(null); })
      .finally(() => { if (!cancelado) setLoading(false); });

    carregarMensagens();
    ultimoMarcarLidoRef.current = 0;
    marcarLidoThrottled(conversaId);
    proximoDoFundoRef.current = true;
    return () => { cancelado = true; };
  }, [sessaoId, conversaId, carregarMensagens, marcarLidoThrottled]);

  useEffect(() => {
    if (mensagens.length > 0 && primeiraVez.current) {
      primeiraVez.current = false;
      rolarFundo();
    }
  }, [mensagens, rolarFundo]);

  useEffect(() => {
    if (!hasMore || !topo.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !carregando && mensagens.length > 0 && cursor) {
          carregarMensagens(cursor);
        }
      },
      { root: listRef.current, threshold: 0.1 },
    );
    obs.observe(topo.current);
    return () => obs.disconnect();
  }, [hasMore, carregando, mensagens, cursor, carregarMensagens]);

  useEffect(() => {
    if (!socket || !conversaId) return;
    socket.emit("join-conversa-wa", conversaId);

    function handleMensagemNew(payload) {
      if (!payload || Number(payload.conversa_id) !== Number(conversaId)) return;
      const nova = payload.mensagem ?? payload;
      if (!nova || !nova.id) return;
      setMensagens((prev) => {
        if (prev.some((m) => m.id === nova.id)) return prev;
        if (nova.direcao === "outbound") {
          const idx = prev.findIndex(
            (m) => m._otimista && m.direcao === "outbound" && (m.texto || "") === (nova.texto || "") && m.tipo === nova.tipo,
          );
          if (idx !== -1) {
            const copia = [...prev];
            copia[idx] = nova;
            return copia;
          }
        }
        return [...prev, nova];
      });
      if (payload.conversa && typeof payload.conversa === "object") {
        setConversa((prev) => (prev ? { ...prev, ...payload.conversa } : prev));
      }
      if (nova.direcao === "inbound") marcarLidoThrottled(conversaId);
      if (proximoDoFundoRef.current || nova.direcao === "outbound") rolarFundo();
    }

    function handleMensagemStatus(payload) {
      if (!payload || Number(payload.conversa_id) !== Number(conversaId)) return;
      setMensagens((prev) =>
        prev.map((m) =>
          m.id === payload.mensagem_id
            ? {
                ...m,
                ack: payload.ack ?? m.ack,
                entregue_em: payload.entregue_em ?? m.entregue_em,
                lido_em: payload.lido_em ?? m.lido_em,
              }
            : m,
        ),
      );
    }

    function handleReconnect() {
      socket.emit("join-conversa-wa", conversaId);
      carregarMensagens();
    }

    socket.on("connect", handleReconnect);
    socket.on("mensagem:new", handleMensagemNew);
    socket.on("mensagem:status", handleMensagemStatus);
    return () => {
      socket.off("connect", handleReconnect);
      socket.off("mensagem:new", handleMensagemNew);
      socket.off("mensagem:status", handleMensagemStatus);
      socket.emit("leave-conversa-wa", conversaId);
    };
  }, [socket, conversaId, rolarFundo, marcarLidoThrottled, carregarMensagens]);

  function substituirOtimista(otimistaId, real) {
    setMensagens((prev) => {
      if (real?.id && prev.some((m) => m.id === real.id)) return prev.filter((m) => m.id !== otimistaId);
      return prev.map((m) => (m.id === otimistaId ? { ...real, _otimista: false } : m));
    });
  }

  async function handleEnviarTexto(texto) {
    setEnviando(true);
    const otimista = {
      id: `temp-${Date.now()}`,
      direcao: "outbound",
      tipo: "text",
      texto,
      origem: "chat",
      criado_em: new Date().toISOString(),
      _otimista: true,
    };
    setMensagens((prev) => [...prev, otimista]);
    proximoDoFundoRef.current = true;
    rolarFundo();
    try {
      const real = await enviarTexto(sessaoId, conversaId, texto);
      if (real && real.id) substituirOtimista(otimista.id, real);
    } catch (err) {
      setMensagens((prev) => prev.filter((m) => m.id !== otimista.id));
      throw err;
    } finally {
      setEnviando(false);
    }
  }

  async function handleEnviarMidia(file, legenda) {
    setEnviando(true);
    const tipo = tipoPorMime(file.type);
    const previewUrl = tipo === "image" || tipo === "video" || tipo === "audio" ? URL.createObjectURL(file) : null;
    const otimista = {
      id: `temp-${Date.now()}`,
      direcao: "outbound",
      tipo,
      texto: legenda || "",
      origem: "chat",
      midia_url: previewUrl,
      midia_mime: file.type,
      midia_nome: file.name,
      midia_tamanho: file.size,
      criado_em: new Date().toISOString(),
      _otimista: true,
    };
    setMensagens((prev) => [...prev, otimista]);
    proximoDoFundoRef.current = true;
    rolarFundo();
    try {
      const real = await enviarMidia(sessaoId, conversaId, file, legenda);
      if (real && real.id) substituirOtimista(otimista.id, real);
    } catch (err) {
      setMensagens((prev) => prev.filter((m) => m.id !== otimista.id));
      throw err;
    } finally {
      if (previewUrl) setTimeout(() => URL.revokeObjectURL(previewUrl), 60000);
      setEnviando(false);
    }
  }

  if (!conversaId) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-surface-1/40 text-center px-6">
        <MessageSquare className="w-12 h-12 text-ink-5 mb-4" aria-hidden="true" />
        <p className="text-ink-4 text-sm font-medium">Selecione uma conversa</p>
        <p className="text-ink-5 text-xs mt-1">Escolha uma conversa na lista para ver o histórico desta sessão</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-surface-1/40">
        <Loader2 className="w-6 h-6 animate-spin text-ink-5" aria-label="Carregando conversa" />
      </div>
    );
  }

  const nome = nomeConversa(conversa);
  const subtitulo = conversa?.is_grupo
    ? "Grupo"
    : [formatarTelefone(conversa?.telefone), conversa?.cliente_nome && conversa.cliente_nome !== nome ? conversa.cliente_nome : null]
        .filter(Boolean)
        .join(" · ");

  return (
    <div className="flex flex-col h-full bg-surface-1">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-white/[0.06] bg-base flex-shrink-0">
        {onVoltar && (
          <button
            type="button"
            onClick={onVoltar}
            aria-label="Voltar para lista de conversas"
            className="flex md:hidden text-ink-3 hover:text-ink-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded p-1 -ml-1 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
        )}
        <Avatar nome={nome} grupo={conversa?.is_grupo} size="w-8 h-8 text-xs" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-ink-1 truncate leading-tight flex items-center gap-1.5">
            {conversa?.is_grupo && <Users className="w-3.5 h-3.5 text-ink-4" />}
            <span className="truncate">{nome}</span>
          </p>
          {subtitulo && <p className="text-xs text-ink-4 truncate">{subtitulo}</p>}
        </div>
        {conversa?.modo === "humano" && <span className="chip-warning">Atendimento humano</span>}
      </div>

      <div
        ref={listRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto scrollbar-thin py-2"
        aria-label="Histórico de mensagens"
        aria-live="polite"
      >
        <div ref={topo} className="h-1" aria-hidden="true" />
        {carregando && mensagens.length > 0 && (
          <div className="flex justify-center py-2">
            <Loader2 className="w-4 h-4 animate-spin text-ink-5" aria-label="Carregando mensagens anteriores" />
          </div>
        )}
        {!carregando && mensagens.length === 0 && (
          <div className="flex flex-col items-center justify-center h-32 text-center px-4">
            <p className="text-xs text-ink-5 italic">Nenhuma mensagem gravada ainda.</p>
          </div>
        )}
        {(() => {
          let ultimoDia = null;
          const nodes = [];
          for (const msg of mensagens) {
            const ts = timestampMsg(msg);
            const dia = chaveDia(ts);
            if (dia && dia !== ultimoDia) {
              nodes.push(<DiaSeparador key={`dia-${dia}-${msg.id ?? ""}`} iso={ts} />);
              ultimoDia = dia;
            }
            nodes.push(<MessageBubble key={msg.id} mensagem={msg} grupo={Boolean(conversa?.is_grupo)} />);
          }
          return nodes;
        })()}
      </div>

      <ChatInput
        enviando={enviando}
        onEnviarTexto={handleEnviarTexto}
        onEnviarMidia={handleEnviarMidia}
        desabilitado={false}
        motivoDesabilitado={gravacaoDesligada ? "Gravação desligada nesta sessão: o envio funciona, mas o histórico não será salvo." : null}
      />
    </div>
  );
}
